/**
 * AIThreadSummary
 *
 * Thread summarization panel at the top of ThreadView.
 * Shows summary, action items, and key decisions.
 */

import { useState } from 'react';
import {
  Sparkles,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Loader2,
  CheckSquare,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAISummary } from '../hooks/useAISummary';

interface AIThreadSummaryProps {
  accountId: string;
  threadId: string;
}

export function AIThreadSummary({ accountId, threadId }: AIThreadSummaryProps) {
  const { summary, loading, summarize, refresh } = useAISummary(accountId, threadId);
  const [expanded, setExpanded] = useState(true);

  if (!summary && !loading) {
    return (
      <div className="px-4 py-2 border-b border-border bg-muted/30">
        <Button
          variant="ghost"
          size="sm"
          className="text-xs gap-1.5"
          onClick={summarize}
          disabled={loading}
        >
          <Sparkles className="h-3.5 w-3.5" /> Summarize thread
        </Button>
      </div>
    );
  }

  if (loading && !summary) {
    return (
      <div className="px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Generating summary...
        </div>
      </div>
    );
  }

  if (!summary) return null;

  return (
    <div className="border-b border-border bg-muted/30">
      {/* Header */}
      <button
        className="flex items-center gap-2 w-full px-4 py-2 text-left hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <Sparkles className="h-3.5 w-3.5 text-primary" />
        <span className="text-xs font-medium">AI Summary</span>
        <span className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              refresh();
            }}
            disabled={loading}
            title="Refresh summary"
          >
            {loading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
          </Button>
        </span>
      </button>

      {/* Content */}
      {expanded && (
        <div className="px-4 pb-3 space-y-3">
          {/* Summary */}
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <MessageSquare className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Summary</span>
            </div>
            <p className="text-sm">{summary.summary}</p>
          </div>

          {/* Action Items */}
          {summary.actionItems.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <CheckSquare className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground">Action Items</span>
              </div>
              <ul className="space-y-1">
                {summary.actionItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <input type="checkbox" className="mt-1 rounded" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Key Decisions */}
          {summary.keyDecisions.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <Sparkles className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground">Key Decisions</span>
              </div>
              <ul className="space-y-1">
                {summary.keyDecisions.map((decision, i) => (
                  <li key={i} className="text-sm flex items-start gap-1.5">
                    <span className="text-muted-foreground">•</span>
                    <span>{decision}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
